import ApiService from './';
import { AddDealReminderParams, DeleteDealReminderParams, DuplicatePipelineParams, EditPipelineParams, PipelineParams, PipelineResponse } from '../params/pipeline';

/**
 * @heading1: Pipeline API service.
 *
 * Provides a collection of methods to interact with the Pipeline API.
 * Includes operations for managing pipelines, stages, deals, and related functionalities.
 */

const PipelineApi = {
    /**
     * @heading3: Create Pipeline
        * @paragraph: Sends a POST request to create a new pipeline.
        * @param: {string} token - The user's authentication token.
        * @param: {PipelineParams} data - The data required to create a pipeline.
        * @returns: {Promise<PipelineResponse>} - The response containing the pipeline details.
     * @separator:
     */
    createPipeline: (token: string, data: PipelineParams): Promise<PipelineResponse> => {
        // Perform a POST request to the login endpoint with the user data
        return ApiService.apiHit<PipelineResponse>({
            url: 'pipeline',
            method: 'post',
            token,
            data,
            getIp: {
                get: true,
                fieldValue: 'user_ip'
            }
        });
    },
    /**
 * @heading3: Get Pipelines
 * @paragraph: Sends a GET request to retrieve all pipelines for the user.
 * @param: {string} token - The user's authentication token.
 * @returns: {Promise<PipelineResponse>} - The response containing the list of pipelines.
 */
    getPipelines: (token: string, client_id: number): Promise<PipelineResponse> => {
        return ApiService.apiHit({
            url: 'get-pipeline',
            method: 'post',
            token,
            data: { client_id },
            getIp: {
                get: true,
                fieldValue: 'user_ip'
            }
        });
    },
    /**
 * @heading3: Get Single Pipeline Detail
 * @paragraph: Sends a GET request to fetch details of a single pipeline by its ID.
 * @param: {string} token - The user's authentication token.
 * @param: {number} id - The ID of the pipeline to retrieve.
 */
    getSinglePipelineDetail: (token: string, id: number) => {
        return ApiService.apiHit({
            url: `pipeline/${id}`,
            method: 'get',
            token
        });
    },
    /**
 * @heading3: Edit Pipeline
 * @paragraph: Sends a POST request to edit the details of an existing pipeline.
 * @param: {string} token - The user's authentication token.
 * @param: {EditPipelineParams} data - The updated pipeline details.
 */
    editPipeline: (token: string, data: EditPipelineParams) => {
        return ApiService.apiHit({
            url: 'pipeline-edit',
            method: 'post',
            token,
            data,
            getIp: {
                get: true,
                fieldValue: 'user_ip'
            }
        });
    },
    /**
 * @heading3: Delete Pipeline
 * @paragraph: Sends a POST request to delete a pipeline by its ID.
 * @param: {string} token - The user's authentication token.
 * @param: {number} id - The ID of the pipeline to delete.
 */
    deletePipeline: (token: string, id: number) => {
        const data = {
            id
        };
        return ApiService.apiHit({
            url: 'pipeline-delete',
            method: 'post',
            token,
            data,
            getIp: {
                get: true,
                fieldValue: 'user_ip'
            }
        });
    },
    /**
 * @heading3: Duplicate Pipeline
 * @paragraph: Sends a POST request to duplicate an existing pipeline.
 * @param: {string} token - The user's authentication token.
 * @param: {DuplicatePipelineParams} data - The parameters for duplicating the pipeline.
 */
    duplicatePipeline: (token: string, data: DuplicatePipelineParams) => {
        return ApiService.apiHit({
            url: 'duplicate-pipeline',
            method: 'post',
            token,
            data,
            getIp: {
                get: true,
                fieldValue: 'user_ip'
            }
        });
    },
    /**
 * @heading3: Get Deal Reminders
 * @paragraph: Sends a POST request to retrieve reminders for a specific deal.
 * @param: {string} token - The user's authentication token.
 * @param: {number} id - The ID of the deal to fetch reminders for.
 */
    getDealsReminders: (token: string, id: number) => {
        return ApiService.apiHit({
            url: 'deal-reminders',
            method: 'post',
            token,
            data: {
                id
            },
            getIp: {
                get: true,
                fieldValue: 'user_ip'
            }
        });
    },
    /**
 * @heading3: Add Deal Reminder
 * @paragraph: Sends a POST request to add a reminder to a specific deal.
 * @param: {string} token - The user's authentication token.
 * @param: {AddDealReminderParams} data - The reminder data to be added.
 */
    addDealReminder: (token: string, data: AddDealReminderParams) => {
        return ApiService.apiHit({
            url: 'add-deal-reminders',
            method: 'post',
            token,
            data,
            getIp: {
                get: true,
                fieldValue: 'user_ip'
            }
        });
    },
    /**
 * @heading3: Delete Deal Reminder
 * @paragraph: Sends a POST request to delete a reminder from a specific deal.
 * @param: {string} token - The user's authentication token.
 * @param: {DeleteDealReminderParams} data - The reminder ID to delete.
 */
    deleteDealReminder: (token: string, data: DeleteDealReminderParams) => {
        return ApiService.apiHit({
            url: 'delete-deal-reminder',
            method: 'post',
            token,
            data,
            getIp: {
                get: true,
                fieldValue: 'user_ip'
            }
        });
    },
    bulkDealTransfer: (token: string, deal_ids: number[], stage_id: number) => {
        return ApiService.apiHit({
            url: 'bulk-stage-transfer',
            method: 'post',
            token,
            data: { deal_ids, stage_id },
            getIp: {
                get: true,
                fieldValue: 'user_ip'
            }
        });
    },
    bulkDealDelete: (token: string, deal_ids: number[]) => {
        return ApiService.apiHit({
            url: 'bulk-deal-delete',
            method: 'post',
            token,
            data: { deal_ids },
            getIp: {
                get: true,
                fieldValue: 'user_ip'
            }
        });
    },
    bulkDealArchive: (token: string, deal_ids: number[]) => {
        return ApiService.apiHit({
            url: 'bulk-deal-archived',
            method: 'post',
            token,
            data: { deal_ids },
            getIp: {
                get: true,
                fieldValue: 'user_ip'
            }
        });
    },
    fetchPipelineTemplates: (token: string) => {
        return ApiService.apiHit({
            url: 'get-pipeline-templates',
            method: 'post',
            token,
            data: {},
            getIp: {
                get: true,
                fieldValue: 'user_ip'
            }
        });
    }
};

export default PipelineApi;
